import { useMemo } from "react";
import { useCustom, type HttpError } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { ENV } from "../config/env";
import type { TocMenuItem, TocMenuResponse } from "../types/menu";

/**
 * Check if we should show all menu items or only user-created ones.
 * Default: only show user-created items (from TOC Editor)
 */
const showAllMenuItems = (): boolean => {
  return import.meta.env.VITE_SHOW_ALL_MENU_ITEMS === "true";
};

/**
 * Aras Innovator TOC root categories to exclude from the dashboard.
 * Configurable via VITE_EXCLUDED_TOC_NAMES env var (comma-separated).
 * Defaults to standard Aras PLM/system categories not relevant to custom modules.
 */
const EXCLUDED_ARAS_TOC_NAMES = new Set(ENV.EXCLUDED_TOC_NAMES);

/**
 * Filter out default Aras TOC categories.
 * Only filters root-level items; children of non-excluded items are kept intact.
 */
const filterExcludedArasItems = (items: TocMenuItem[]): TocMenuItem[] => {
  return items.filter((item) => !EXCLUDED_ARAS_TOC_NAMES.has(item.name));
};

/**
 * Filter menu items to only include user-created items and their ancestors.
 *
 * Logic:
 * - Keep items where isUserCreated is true
 * - If a parent category is user-created, keep ALL its children
 * - Keep parent categories that have user-created descendants
 *   (so user-created items nested under built-in categories remain visible)
 */
const filterUserCreatedItems = (
  items: TocMenuItem[],
  parentIsUserCreated = false,
): TocMenuItem[] => {
  return items
    .map((item) => {
      // If parent is user-created, keep all children as-is
      if (parentIsUserCreated) {
        return item;
      }

      // Recursively filter children first to check for user-created descendants
      const filteredChildren = item.children
        ? filterUserCreatedItems(item.children, item.isUserCreated)
        : [];

      return { ...item, children: filteredChildren };
    })
    .filter(
      (item) =>
        item.isUserCreated ||
        parentIsUserCreated ||
        (item.children && item.children.length > 0),
    );
};

/**
 * Filter menu items to only show those matching VITE_VISIBLE_MENU_ITEMS (whitelist).
 * Supports up to 3-level sub-menu configuration.
 *
 * Matching (case-insensitive):
 * - ItemType items: matched by `itemTypeName`
 * - Categories: matched by `label` or `name`
 * - If a leaf item matches → parent categories are auto-included
 * - If a category matches → the category AND all its children are shown
 * - Empty categories (no visible descendants) are pruned
 *
 * Special values:
 * - "*" or empty list → show all items
 */
export const filterVisibleMenuItems = (
  items: TocMenuItem[],
  visibleItems: string[],
): TocMenuItem[] => {
  // Show all if empty or contains "*"
  if (visibleItems.length === 0 || visibleItems.includes("*")) return items;

  const visibleSet = new Set(visibleItems.map((s) => s.toLowerCase()));

  const isDirectMatch = (item: TocMenuItem): boolean => {
    if (item.itemTypeName && visibleSet.has(item.itemTypeName.toLowerCase())) {
      return true;
    }
    if (item.label && visibleSet.has(item.label.toLowerCase())) {
      return true;
    }
    if (item.name && visibleSet.has(item.name.toLowerCase())) {
      return true;
    }
    return false;
  };

  const recurse = (list: TocMenuItem[]): TocMenuItem[] =>
    list
      .map((item) => {
        // Direct match → keep item with ALL children intact
        if (isDirectMatch(item)) {
          return item;
        }

        // Not a direct match — check if any descendant matches
        if (item.children && item.children.length > 0) {
          const filteredChildren = recurse(item.children);
          if (filteredChildren.length > 0) {
            return { ...item, children: filteredChildren };
          }
        }

        // No match at all → exclude
        return null;
      })
      .filter((item): item is TocMenuItem => item !== null);

  return recurse(items);
};

interface UseMenuFromApiResult {
  /** Menu items from API (hierarchical), filtered based on VITE_SHOW_ALL_MENU_ITEMS */
  menuItems: TocMenuItem[];
  /** All menu items from API (unfiltered) */
  allMenuItems: TocMenuItem[];
  /** Loading state */
  isLoading: boolean;
  /** Whether the API has been called successfully at least once */
  isFetched: boolean;
  /** Error state */
  isError: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch menu from API */
  refetch: () => void;
}

/**
 * Hook to fetch TOC menu from API.
 * Uses Refine's useCustom (React Query) for automatic cache management.
 *
 * Caching strategy:
 * - staleTime: 5 minutes — menu data considered fresh, no refetch
 * - refetchOnWindowFocus: true — auto-refresh when user returns to tab
 *   (only if data is stale, i.e. older than staleTime)
 *
 * This eliminates the need for Ctrl+Shift+R when menu config changes.
 *
 * Menu items are filtered based on VITE_SHOW_ALL_MENU_ITEMS env variable:
 * - "true": Show all menu items
 * - "false" or undefined: Only show user-created items from TOC Editor
 */
export const useMenuFromApi = (): UseMenuFromApiResult => {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;

  const isAuthenticated = !!localStorage.getItem("access_token");

  const {
    data: tocData,
    isLoading,
    isError,
    error,
    refetch,
  } = useCustom<TocMenuResponse>({
    url: "/menu/toc",
    method: "get",
    config: {
      query: { lang: currentLanguage },
    },
    queryOptions: {
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000, // 5 minutes — menu stays fresh
      refetchOnWindowFocus: true, // auto-refresh when user returns to tab
    },
  });

  const allMenuItems = tocData?.data?.items ?? [];
  const isFetched = !isLoading && !!tocData;

  // Filter menu items:
  // 1. Always exclude default Aras TOC categories (Administration, Design, etc.)
  // 2. If VITE_SHOW_ALL_MENU_ITEMS is "true", show remaining items
  // 3. Otherwise, filter to user-created items only (with fallback to all remaining)
  // 4. Always apply VITE_VISIBLE_MENU_ITEMS whitelist filter last
  const menuItems = useMemo(() => {
    const withoutArasDefaults = filterExcludedArasItems(allMenuItems);

    let result: TocMenuItem[];
    if (showAllMenuItems()) {
      result = withoutArasDefaults;
    } else {
      const filtered = filterUserCreatedItems(withoutArasDefaults);
      result =
        filtered.length === 0 && withoutArasDefaults.length > 0
          ? withoutArasDefaults
          : filtered;
    }

    return filterVisibleMenuItems(result, ENV.VISIBLE_MENU_ITEMS);
  }, [allMenuItems]);

  return {
    menuItems,
    allMenuItems,
    isLoading: isAuthenticated && isLoading,
    isFetched,
    isError,
    error: (error as HttpError)?.message ?? null,
    refetch,
  };
};

/**
 * Hook that uses Refine's useCustom for fetching menu.
 * Use this when inside Refine context (e.g., in CustomSider).
 */
export { useMenuFromApi as default };
